import type { LocalDate } from '../dates';
import type { Mutation } from '../mutations';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import type { AccountEntryLike } from './accounts';
import { isValidLocalDate } from './schedule';
import { budgetMutation, type BudgetResult } from './types';

/**
 * v0.6 "Owed to you" (Ivan, 2026-11-03): money someone owes you. It is never in Safe to spend,
 * income or the cash flow. Each receipt (partial or whole) is a deposit into the chosen account:
 * a ledger row of kind transfer with only toAccountId and sourceId = the receivable, excluded
 * from income and spending like Coming in. What's left to receive is derived from those rows.
 */
export interface ReceivableLike {
  id: string;
  /** Who owes. */
  name: string;
  amountMinor: number;
  expectedDate: LocalDate | null;
  note: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export const MAX_RECEIVABLE_NAME = 60;
export const MAX_RECEIVABLE_NOTE = 200;

/** A receipt row as receivables read it. */
export type ReceiptEntryLike = Pick<
  AccountEntryLike,
  'id' | 'date' | 'amountMinor' | 'kind' | 'sourceModule' | 'sourceId' | 'toAccountId' | 'deletedAt'
>;

export interface ReceivableStatus {
  receivable: ReceivableLike;
  receivedMinor: number;
  /** amount − received, never below 0. */
  remainingMinor: number;
  settled: boolean;
  /** Live receipts, newest first. */
  receipts: ReceiptEntryLike[];
}

/** Is this ledger row a receipt of that receivable? */
export function isReceiptOf(e: ReceiptEntryLike, receivableId: string): boolean {
  return (
    e.deletedAt === null &&
    e.kind === 'transfer' &&
    e.sourceModule === 'budget' &&
    e.sourceId === receivableId
  );
}

export function receivableStatus(
  r: ReceivableLike,
  entries: readonly ReceiptEntryLike[],
): ReceivableStatus {
  const receipts = entries
    .filter((e) => isReceiptOf(e, r.id))
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const receivedMinor = receipts.reduce((s, e) => s + e.amountMinor, 0);
  const remainingMinor = Math.max(0, r.amountMinor - receivedMinor);
  return { receivable: r, receivedMinor, remainingMinor, settled: remainingMinor === 0, receipts };
}

/** Live receivables with what's left: open ones first (by expected day, undated last), then settled. */
export function receivableStatuses(
  items: readonly ReceivableLike[],
  entries: readonly ReceiptEntryLike[],
): ReceivableStatus[] {
  const key = (s: ReceivableStatus) => s.receivable.expectedDate ?? '9999-12-31';
  return items
    .filter((r) => r.deletedAt === null)
    .map((r) => receivableStatus(r, entries))
    .sort(
      (a, b) =>
        Number(a.settled) - Number(b.settled) ||
        key(a).localeCompare(key(b)) ||
        a.receivable.createdAt.localeCompare(b.receivable.createdAt),
    );
}

/** "Owed to you" total: what's still to receive. Never added to Safe to spend. */
export function owedToYouMinor(statuses: readonly ReceivableStatus[]): number {
  return statuses.reduce((s, r) => s + r.remainingMinor, 0);
}

// ---------- Builders ----------

export interface ReceivableInput {
  name: string;
  amountMinor: number;
  expectedDate: LocalDate | null;
  note: string | null;
}

export type ReceivableError =
  | 'name_required'
  | 'name_too_long'
  | 'note_too_long'
  | 'bad_amount'
  | 'bad_date'
  | 'over_remaining'
  | 'not_found';

const validAmount = (n: number) => Number.isInteger(n) && n > 0 && n <= MAX_AMOUNT_MINOR;
const clean = (t: string) => t.trim().replace(/\s+/g, ' ');

function validate(input: ReceivableInput): ReceivableError | null {
  const name = clean(input.name);
  if (name === '') return 'name_required';
  if (name.length > MAX_RECEIVABLE_NAME) return 'name_too_long';
  if (clean(input.note ?? '').length > MAX_RECEIVABLE_NOTE) return 'note_too_long';
  if (!validAmount(input.amountMinor)) return 'bad_amount';
  if (input.expectedDate !== null && !isValidLocalDate(input.expectedDate)) return 'bad_date';
  return null;
}

function values(input: ReceivableInput): Record<string, unknown> {
  const note = clean(input.note ?? '');
  return {
    name: clean(input.name),
    amountMinor: input.amountMinor,
    expectedDate: input.expectedDate,
    note: note === '' ? null : note,
  };
}

export function buildAddReceivable(
  input: ReceivableInput,
  ctx: BuildCtx,
): BudgetResult<{ id: string; mutation: Mutation }, ReceivableError> {
  const error = validate(input);
  if (error) return { ok: false, error };
  const id = ctx.newId();
  return {
    ok: true,
    id,
    mutation: budgetMutation(ctx, 'receivables', 'insert', id, {
      ...values(input),
      currency: 'PHP',
    }),
  };
}

/** Writes only the fields that changed. */
export function buildEditReceivable(
  before: ReceivableLike,
  input: ReceivableInput,
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation | null }, ReceivableError> {
  const error = validate(input);
  if (error) return { ok: false, error };
  const next = values(input);
  const changed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    if ((before as unknown as Record<string, unknown>)[k] !== v) changed[k] = v;
  }
  if (Object.keys(changed).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: budgetMutation(ctx, 'receivables', 'update', before.id, changed),
  };
}

/** Soft delete; its receipts go with it (and come back on restore), like Coming in. */
export function buildDeleteReceivable(
  r: ReceivableLike,
  receiptIds: readonly string[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    budgetMutation(ctx, 'receivables', 'delete', r.id),
    ...receiptIds.map((id) => budgetMutation(ctx, 'ledger_entries', 'delete', id)),
  ];
}

export function buildRestoreReceivable(
  r: ReceivableLike,
  receiptIds: readonly string[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    budgetMutation(ctx, 'receivables', 'insert', r.id, {}),
    ...receiptIds.map((id) => budgetMutation(ctx, 'ledger_entries', 'insert', id, {})),
  ];
}

/**
 * "Received" (part or all): a deposit into the account, never income. More than what's left is
 * refused (the amount owed can be edited instead). Each receipt is its own row (a fresh id), so
 * two partial payments are two rows.
 */
export function buildReceiveReceivable(
  status: Pick<ReceivableStatus, 'receivable' | 'remainingMinor'>,
  got: { amountMinor: number; date: LocalDate; accountId: string | null },
  ctx: BuildCtx,
): BudgetResult<{ id: string; mutation: Mutation }, ReceivableError> {
  const r = status.receivable;
  if (r.deletedAt !== null) return { ok: false, error: 'not_found' };
  if (!validAmount(got.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (got.amountMinor > status.remainingMinor) return { ok: false, error: 'over_remaining' };
  if (!isValidLocalDate(got.date)) return { ok: false, error: 'bad_date' };
  const id = ctx.newId();
  return {
    ok: true,
    id,
    mutation: budgetMutation(ctx, 'ledger_entries', 'insert', id, {
      date: got.date,
      amountMinor: got.amountMinor,
      currency: 'PHP',
      kind: 'transfer',
      categoryId: null,
      method: null,
      fundId: null,
      accountId: null,
      toAccountId: got.accountId,
      sourceModule: 'budget',
      sourceId: r.id,
      note: `From ${r.name}`,
      recordedTz: ctx.tz,
    }),
  };
}

/** Undo one receipt: the row is soft-deleted (History keeps it). */
export function buildUndoReceipt(receiptId: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'ledger_entries', 'delete', receiptId);
}

export function buildRestoreReceipt(receiptId: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'ledger_entries', 'insert', receiptId, {});
}
